import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Experiences from './components/Experiences';
import Footer from './components/Footer';
import BookingModal from './components/BookingModal';
import NurseryShop from './components/NurseryShop';
import AdminDashboard from './components/AdminDashboard';
import CartDrawer from './components/CartDrawer';
import OrderSuccess from './components/OrderSuccess';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import { nurseryGalleryImages } from './data/mock';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './components/ui/carousel';
import './App.css';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function HomePage({ onBookClick }) {
  return (
    <>
      <Hero onBookClick={onBookClick} />
      <ShopNurseryPortal />
      <NurseryGallery />
      <About />
      <Experiences />
      <CallToAction onBookClick={onBookClick} />
      <Footer />
    </>
  );
}

function NurseryGallery() {
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h3 
            className="text-2xl sm:text-3xl font-bold text-black mb-3"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            The Nursery
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Check out our ever-growing collection of native plants, rare specimens, and sustainably grown treasures.
          </p>
        </div>
        <div className="px-2 sm:px-8">
          <Carousel className="w-full">
            <CarouselContent>
              {nurseryGalleryImages.map((image) => (
                <CarouselItem key={image.id} className="md:basis-1/2 lg:basis-1/3">
                  <div className="relative overflow-hidden rounded-xl group cursor-pointer" style={{ height: '280px' }}>
                    <img 
                      src={image.url}
                      alt={image.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-4">
                      <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {image.alt}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="-left-4 sm:left-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
            <CarouselNext className="-right-4 sm:right-0 h-12 w-12 bg-black/70 hover:bg-black text-white border-none shadow-lg" />
          </Carousel>
        </div>
      </div>
    </section>
  );
}

function ShopNurseryPortal() {
  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <a 
          href="/nursery-shop"
          className="block cursor-pointer group"
        >
          <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-200">
            <div className="p-6 sm:p-8 text-center bg-black">
              <div className="flex items-center justify-center gap-3 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                <h3 
                  className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
                  style={{ fontFamily: "'Crimson Text', serif" }}
                >
                  Shop Our Nursery
                </h3>
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
              </div>
              <p className="text-gray-300 text-lg">
                Browse a few of our online selections — with more to choose from in person
              </p>
            </div>
            <div className="relative h-48 sm:h-64 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_greenerside/artifacts/yh9wimad_1000001707.jpg"
                alt="Plant Nursery"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="inline-block bg-black text-white px-6 py-3 rounded-full font-bold text-lg group-hover:bg-white group-hover:text-black transition-all duration-300">
                  Enter Shop →
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>
    </section>
  );
}

function CallToAction({ onBookClick }) {
  return (
    <section id="book-visit-cta" className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white">
      <div className="max-w-4xl mx-auto text-center">
        <h2 
          className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6"
          style={{ fontFamily: "'Crimson Text', serif" }}
        >
          Ready to Experience Permaculture?
        </h2>
        <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          Deepen your understanding of permaculture principles, attend workshops, and connect with sustainability experts. Book a visit to explore our farm and learn regenerative practices.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button 
            onClick={onBookClick}
            className="bg-white text-black hover:bg-gray-100 transition-all duration-200 text-lg px-8 py-4 rounded-md font-medium"
          >
            Book Your Visit
          </button>
          <button 
            onClick={() => {
              const element = document.getElementById('about');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black transition-all duration-200 text-lg px-8 py-4 rounded-md font-medium"
          >
            Learn More
          </button>
        </div>
      </div>
    </section>
  );
}

function App() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  return (
    <CartProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Header onBookClick={() => setIsBookingModalOpen(true)} />
          <CartDrawer />
          <Routes>
            <Route path="/" element={<HomePage onBookClick={() => setIsBookingModalOpen(true)} />} />
            <Route path="/nursery-shop" element={<NurseryShop />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/order-success" element={<OrderSuccess />} />
          </Routes>
          <BookingModal 
            isOpen={isBookingModalOpen} 
            onClose={() => setIsBookingModalOpen(false)} 
          />
          <Toaster position="top-right" />
        </div>
      </Router>
    </CartProvider>
  );
}

export default App;
